import { type ReactNode, useState } from 'react';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { ContextualHelp } from '@gonfalon/launchpad-experimental/ContextualHelp';
import { type ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';
import { Box } from '@launchpad-ui/box';
import { Button, Perceivable, Pressable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { type UseAccessCheckProps, useAccessCheck } from '../../hooks/useAccessCheck';
import type { AccessCheck as AccessCheckType } from '../../internal/types';

import { policyContainsPartial } from './resourceContainsPartial';
import { RestrictProvider } from './RestrictProvider';

export type AccessCheckProps<T extends ResourceSpecifier['type']> = UseAccessCheckProps<T> & {
  /**
   * The child element(s) to render. Can be a ReactNode or a function that returns a ReactNode.
   * The function will be called with the access object which contains the allowed status.
   */
  children: ReactNode | (({ access }: { access?: AccessCheckType }) => ReactNode);
  /**
   * If true, the control will not be rendered until the access check is ready.
   */
  hideControlUntilReady?: boolean;
  /**
   * If true, the control will not be rendered if the access is not allowed.
   */
  hideControlIfNotAllowed?: boolean;
};

const FALLBACK_TOOLTIP_MESSAGE = 'Could not verify permissions. Please refresh and try again.';

export function AccessCheck<T extends ResourceSpecifier['type']>(props: AccessCheckProps<T>) {
  return (
    <ErrorBoundary severity="medium" fallbackRender={() => <AccessCheckErrorFallback {...props} />}>
      <AccessCheckInner {...props} />
    </ErrorBoundary>
  );
}

function AccessCheckErrorFallback<T extends ResourceSpecifier['type']>({
  children,
  hideControlIfNotAllowed = false,
}: AccessCheckProps<T>) {
  if (hideControlIfNotAllowed) {
    return null;
  }

  // Fail-closed: synthesize a denied access object and render the wrapped
  // control disabled. Children rendered as a function get the same shape they
  // would on a real denial, so consumer-side branching still works.
  const fallbackAccess: AccessCheckType = {
    isAllowed: false,
    customMessage: FALLBACK_TOOLTIP_MESSAGE,
  };
  const child = typeof children === 'function' ? children({ access: fallbackAccess }) : children;

  return (
    <RestrictProvider isDisabled>
      <Perceivable isDisabled>
        <TooltipTrigger>
          <Pressable>
            <span role="button" style={{ display: 'inline-block', width: 'max-content', maxWidth: '100%' }}>
              {child}
            </span>
          </Pressable>
          <Tooltip placement="bottom">{FALLBACK_TOOLTIP_MESSAGE}</Tooltip>
        </TooltipTrigger>
      </Perceivable>
    </RestrictProvider>
  );
}

function AccessCheckInner<T extends ResourceSpecifier['type']>({
  children,
  resourceName = '*',
  resourceTags = [],
  hideControlUntilReady = false,
  hideControlIfNotAllowed = false,
  ...props
}: AccessCheckProps<T>) {
  const [bypassDisabled, setBypassDisabled] = useState(false);

  // Use the headless hook for access check logic
  const {
    access,
    isDisabled: hookIsDisabled,
    isLoading,
    error,
    data,
  } = useAccessCheck({
    resourceName,
    resourceTags,
    ...props,
  } as UseAccessCheckProps<T>);

  const isDisabled = bypassDisabled ? false : hookIsDisabled;

  function renderPressable() {
    return (
      <Pressable>
        <span role="button" style={{ display: 'inline-block', width: 'max-content', maxWidth: '100%' }}>
          {child}
        </span>
      </Pressable>
    );
  }

  const child = typeof children === 'function' ? children({ access }) : children;
  const isPartialDenied = checkPartialDenial(data, access);

  if (hideControlUntilReady && isLoading) {
    return null;
  }

  if (hideControlIfNotAllowed && !access.isAllowed) {
    return null;
  }

  /**
   * There are cases when a resource string contains partials (such as '*;tag1,tag2' or '_*').
   * In these cases, access will be denied, but we can bypass the restriction by clicking a button.
   */
  if (!isLoading && !error && isPartialDenied && !bypassDisabled) {
    return (
      <Box display="flex" alignItems="center" gap="$200">
        <Perceivable isDisabled>{renderPressable()}</Perceivable>
        <ContextualHelp>
          <Box maxWidth="$208">
            You are unable to perform this action. If you believe this is an error, click below to continue.
            <br />
            <Button variant="primary" size="small" onPress={() => setBypassDisabled(true)}>
              Continue
            </Button>
          </Box>
        </ContextualHelp>
      </Box>
    );
  }

  return (
    <RestrictProvider isDisabled={isDisabled}>
      {isDisabled && !access.isAllowed ? (
        <Perceivable isDisabled>
          <TooltipTrigger>
            {renderPressable()}
            <Tooltip placement="bottom">{access.customMessage}</Tooltip>
          </TooltipTrigger>
        </Perceivable>
      ) : (
        child
      )}
    </RestrictProvider>
  );
}

function checkPartialDenial(
  data: { allowed?: boolean; roleMaybeAllowsAccess?: { policy?: unknown } } | undefined,
  access: { isAllowed: boolean },
): boolean {
  if (!data || access.isAllowed || !data.roleMaybeAllowsAccess) {
    return false;
  }

  const { policy } = data.roleMaybeAllowsAccess;
  return Array.isArray(policy) && policyContainsPartial(policy as Parameters<typeof policyContainsPartial>[0]);
}
